import { styled } from '../../../theme/stitches.config';
import type * as Stitches from '@stitches/react';

export const SMPText = styled.withConfig({ componentId: 'CommonWidget', displayName: 'TextContainer' })('span', {
  // Reset
  lineHeight: '1',
  margin: '0',
  fontWeight: 400,
  fontVariantNumeric: 'tabular-nums',
  fontFamily: '$primaryFontFamily',

  '&.with-winner-badge': {
    maxWidth: '110px',
  },

  '&.with-ellipsis': {
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },

  variants: {
    gap: {
      mobile: {
        gap: ' 12px',
      },
      desktop: {
        gap: ' 16px',
      },
    },
    display: {
      block: {
        display: 'block',
      },
      inline: {
        display: 'inline',
      },
      none: {
        display: 'none',
      },
      flex: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      },
    },
    padding: {
      'pt-1': {
        pt: '$1',
      },
      'pt-3': {
        pt: '$3',
      },
      none: {
        p: '0px',
      },
    },
    size: {
      '1': {
        fontSize: '$1',
      },
      '2': {
        fontSize: '$2',
      },
      '2_5': {
        fontSize: '$2_5',
      },
      '3': {
        fontSize: '$3',
      },
      '4': {
        fontSize: '$4',
      },
      '5': {
        fontSize: '$5',
        letterSpacing: '-.015em',
      },
      '6': {
        fontSize: '$6',
        letterSpacing: '-.016em',
      },
      '7': {
        fontSize: '$7',
        letterSpacing: '-.031em',
        textIndent: '-.005em',
      },
      '8': {
        fontSize: '$8',
        letterSpacing: '-.034em',
        textIndent: '-.018em',
      },
      '9': {
        fontSize: '$9',
        letterSpacing: '-.055em',
        textIndent: '-.025em',
      },
      'really-small': {
        fontSize: '4.4px',
      },
    },
    textAlign: {
      left: {
        textAlign: 'left',
      },
      center: {
        textAlign: 'center',
      },
      right: {
        textAlign: 'right',
      },
    },
    weight: {
      400: {
        fontWeight: 400,
      },
      600: {
        fontWeight: 600,
      },
      700: {
        fontWeight: 700,
      },
      900: {
        fontWeight: 900,
      },
    },
    width: {
      5: {
        width: '$5',
      },
    },
    pale: {
      true: {
        color: '$teamLostColor !important',
      },
    },
    variant: {
      tooltipText: {
        color: '#FFFFFF',
      },
      red: {
        color: '$red500',
      },
      blue: {
        color: '$blue500',
      },
      green: {
        color: '$green500',
      },
      orange: {
        color: '$orange500',
      },
      accessibilityPrimaryColor: {
        color: '$scoreAccentColor',
      },

      gray: {
        color: '$gray500',
      },
      primaryGray: {
        color: '$primaryGray',
      },
      contrast: {
        color: '$hiContrast',
      },
      accentText: {
        color: '$accentPrimaryBgColor',
      },
      accent: {
        color: '$accentPrimaryBgColor',
        backgroundColor: '$highlightPrimaryBgColor',
      },
      accentSecondary: {
        color: '$accentPrimaryBgColor',
        backgroundColor: '$accentSecondaryBgColor',
      },
      loContrast: {
        color: '$loContrast',
      },
      oddLabel: {
        fontSize: '12px',
        color: '$oddItemLabelColor',
        fontFamily: '$primaryFontFamily',
        textTransform: 'uppercase',
      },
      oddCoefficient: {
        fontSize: '12px',
        color: '$oddItemCoefficientColor',
        fontWeight: 'bold',
        ta: 'right',
        fontFamily: '$primaryFontFamily',
      },
      live: {
        color: '$liveMatchStatusColor',
      },
      livescoreFullTime: {
        color: '$fullTimeMatchStatusColor',
      },
      tabLabel: {
        color: '$pillTextColor',
      },
      activeTabLabel: {
        color: '$pillActiveTextColor',
        fontWeight: 'bold',
      },
      tabLabelDisabled: {
        color: '$disabledPillTextColor',
      },
      fansUnited1X2Label: {
        lineHeight: 1.3,
        fontSize: '$2',
        color: '$oddItemCoefficientColor',
        textAlign: 'center',
        margin: 'auto',
      },
      tennisSingleEventSeparator: {
        alignItems: 'center',
        color: '$hiContrast',
      },
      tennisSingleEventLabel: {
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        color: '$hiContrast',
      },
      diffContrast: {
        color: '$diffContrastColor',
      },
      showMore: {
        pt: '$4',
        pb: '$4',
        textDecoration: 'underline',
        color: '$hiContrast',
        '&:hover': {
          cursor: 'pointer',
          textDecoration: 'none',
        },
        fontSize: '$3',
      },
      tooltip: {
        color: '$tooltipColor',
      },
      teamWinnerForm: {
        display: 'flex !important',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: '50%',
        size: '$5',
        marginRight: '$3',
        fontSize: '$2',
        color: 'Black',
        fontWeight: 'bold',
      },
      matchesH2HTeamStyle: {
        color: '$hiContrast',
        opacity: '80%',
        fontWeight: '400',
      },
      matchesH2HSelectedTeamStyle: {
        fontWeight: '400',
        color: '$hiContrast',
      },
      scoreHeader: {
        width: '100%',
        overflow: 'hidden',
        whiteSpace: 'nowrap',
        textOverflow: 'ellipsis',
        color: '$hiContrast',
      },
    },
  },
  defaultVariants: {
    size: '$3',
    variant: '$contrast',
    display: 'block',
  },
});

export type StyledSMPTextVariants = Stitches.VariantProps<typeof SMPText>;
